import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Wifi, ArrowRight } from "lucide-react";

const DT_KEY = "energyworld.device_token";
const FG_NAME_KEY = "energyworld.furgone_name";

export function getDeviceToken() {
  return localStorage.getItem(DT_KEY);
}
export function clearDeviceToken() {
  localStorage.removeItem(DT_KEY);
  localStorage.removeItem(FG_NAME_KEY);
}

/**
 * TabletEntry: root of the /tablet flow.
 *  - No device_token → show pair code screen
 *  - Has device_token → redirect to /tablet/home
 */
export default function TabletEntry() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [dt, setDt] = useState(() => getDeviceToken());

  useEffect(() => {
    // If already logged in, warn: tablet flow expects a fresh session
    if (user && dt) { navigate("/tablet/home"); }
    else if (dt) { navigate("/tablet/home"); }
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    const c = code.replace(/\D/g, "");
    if (c.length !== 6) { toast.error("Il codice è di 6 cifre"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tablet/pair", { code: c });
      localStorage.setItem(DT_KEY, r.data.device_token);
      localStorage.setItem(FG_NAME_KEY, r.data.furgone_name || "");
      setDt(r.data.device_token);
      toast.success(`Tablet associato: ${r.data.furgone_name}`);
      navigate("/tablet/home");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-6 text-white safe-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/assets/logo.png" alt="Energy World" className="w-24 h-24 mx-auto mb-4 rounded-2xl shadow-xl bg-white p-2"/>
          <h1 className="heading text-4xl font-extrabold tracking-tight">Energy World</h1>
          <p className="text-blue-200 text-sm mt-1">Operai · Tablet furgone</p>
        </div>
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
          <div className="flex items-center gap-2 text-blue-100 text-sm mb-4">
            <Lock className="w-4 h-4"/> Associa questo tablet a un furgone
          </div>
          <p className="text-blue-50/80 text-xs mb-4">Chiedi a Flavio (amministratore) un codice di associazione a 6 cifre. Una volta associato non dovrai più farlo.</p>
          <label className="text-[10px] uppercase tracking-widest text-blue-200 mb-2 block">Codice di associazione</label>
          <input
            type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full text-center text-4xl tracking-[0.4em] font-bold bg-slate-900/60 border border-white/20 rounded-xl py-4 outline-none focus:border-blue-400"
            placeholder="000000"
            data-testid="tablet-pair-code"
          />
          <button
            onClick={submit} disabled={busy || code.length !== 6}
            className="mt-4 w-full bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition"
            data-testid="tablet-pair-submit">
            {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : <>Associa tablet <ArrowRight className="w-5 h-5"/></>}
          </button>
        </div>
        <div className="text-center mt-6">
          {user ? (
            <button onClick={() => { logout(); }} className="text-blue-200 text-xs inline-flex items-center gap-1 hover:text-white">
              <LogOut className="w-3 h-3"/> Esci utente ({user.name})
            </button>
          ) : (
            <a href="/login" className="text-blue-200 text-xs inline-flex items-center gap-1 hover:text-white">
              <Wifi className="w-3 h-3"/> Accedi con email e password (ufficio)
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
